import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Janitor from '../../../../modules/Janitor';
import analytics from '../../../../modules/analytics';

import { SentimentSurvey } from './SentimentSurvey';

/**
 * Container component to render the Sentiment Survey component
 *
 * @example React:
 * ```
 * {showSurvey && (
 *  <SentimentSurveyContainer
 *    surveyName="my survey name"
 *    questionOne="my first question"
 *    onSurveySubmit={onSurveySubmit}
 *    useStarRatings
 *    useStarRatings
 *  />
 * )}
 * ```
 */
class SentimentSurveyContainer extends React.Component {
  static propTypes = {
    surveyName: PropTypes.string.isRequired,
    useStarRatings: PropTypes.bool,
    onStarSelected: PropTypes.func,
    useScoreRatings: PropTypes.bool,
    onScoreSelected: PropTypes.func,
    questionOne: PropTypes.string.isRequired,
    primaryInputQuestion: PropTypes.string,
    placeholder: PropTypes.string,
    onSurveySubmit: PropTypes.func,
    trackingParams: PropTypes.object,
    adjustForNavBar: PropTypes.bool,
    multiChoiceOptions: PropTypes.array,
    onChoiceSelected: PropTypes.func,
    canSubmit: PropTypes.bool,
    onShowToolTips: PropTypes.func,
    onHideToolTips: PropTypes.func,
  };

  static defaultProps = {
    primaryInputQuestion: '',
    placeholder: 'Please type here',
    useStarRatings: false,
    onStarSelected: () => {},
    useScoreRatings: false,
    onScoreSelected: () => {},
    onSurveySubmit: () => {},
    trackingParams: {},
    adjustForNavBar: false,
    multiChoiceOptions: null,
    onChoiceSelected: () => {},
    canSubmit: false,
    onShowToolTips: () => {},
    onHideToolTips: () => {},
  };

  state = {
    showSurvey: false,
    surveyMinimized: false,
    selectedScore: null,
    ratingStars: null,
    selectedChoice: null,
    surveyComplete: false,
    comment: '',
  };

  janitor = new Janitor();

  componentDidMount() {
    const {
      surveyName,
      trackingParams,
      onHideToolTips,
    } = this.props;

    onHideToolTips();
    analytics.track(`${surveyName}_viewed`, trackingParams);
  }

  componentWillUnmount() {
    const {
      onShowToolTips,
    } = this.props;

    onShowToolTips();
  }

  /**
   * Button to either minimize the survey or expand it
   */
  onMinBtnPress = event => {
    const {
      surveyName,
      trackingParams,
    } = this.props;
    const { surveyMinimized } = this.state;
    const eventName = surveyMinimized ? `${surveyName}_expand` : `${surveyName}_minimize`;
    analytics.track(eventName, trackingParams);
    event.preventDefault(); // need to prevent triggering click event if nested
    this.setState({ surveyMinimized: !surveyMinimized });
  };

  /**
   * Handler to handle selection of scores
   */
  handleScoreSelected = score => {
    this.props.onScoreSelected(score);
    return () => {
      this.setState({ selectedScore: score });
    };
  };

  /**
   * Handler to handle selection of stars
   */
  handleStarSelected = starRating => {
    this.props.onStarSelected(starRating);
    this.setState({ ratingStars: starRating });
  };

  handleMultiChoiceSelected = choice => {
    this.props.onChoiceSelected(choice);
    this.setState({ selectedChoice: choice });
  };

  onChangeText = text => {
    this.setState({ comment: text });
  };

  onSubmit = () => {
    const {
      surveyName,
      trackingParams,
      onSurveySubmit,
      onShowToolTips,
    } = this.props;

    const {
      selectedScore,
      ratingStars,
      selectedChoice,
      comment,
    } = this.state;
    analytics.track(`${surveyName}_complete`, {
      ...trackingParams,
      scoreRating: selectedScore,
      starRating: ratingStars,
      selectedChoice,
      comment,
    });

    onShowToolTips();
    onSurveySubmit({ ...this.state, ...this.props });
    this.setState({ surveyComplete: true });
  };

  render() {
    const {
      useStarRatings,
      useScoreRatings,
      questionOne,
      primaryInputQuestion,
      placeholder,
      adjustForNavBar,
      multiChoiceOptions,
      canSubmit,
    } = this.props;

    const {
      selectedScore,
      ratingStars,
      surveyMinimized,
      surveyComplete,
      selectedChoice,
    } = this.state;

    return (
      <SentimentSurvey
        useStarRatings={useStarRatings}
        useScoreRatings={useScoreRatings}
        questionOne={questionOne}
        primaryInputQuestion={primaryInputQuestion}
        placeholder={placeholder}
        handleScoreSelected={this.handleScoreSelected}
        handleStarSelected={this.handleStarSelected}
        onChangeText={this.onChangeText}
        onSubmit={this.onSubmit}
        onMinBtnPress={this.onMinBtnPress}
        selectedScore={selectedScore}
        ratingStars={ratingStars}
        selectedChoice={selectedChoice}
        surveyMinimized={surveyMinimized}
        surveyComplete={surveyComplete}
        adjustForNavBar={adjustForNavBar}
        multiChoiceOptions={multiChoiceOptions}
        handleMultiChoiceSelected={this.handleMultiChoiceSelected}
        canSubmit={canSubmit}
      />
    );
  }
}

const mapDispatchToProps = dispatch => ({
  onShowToolTips: dispatch.clientNavBar.onShowToolTips,
  onHideToolTips: dispatch.clientNavBar.onHideToolTips,
});

export const ConnectedSentimentSurveyContainer = connect(
  undefined,
  mapDispatchToProps,
)(SentimentSurveyContainer);

export default ConnectedSentimentSurveyContainer;
